import ContactProfileImage from '@app/components/ContactProfileImage'
import ContextReminderContainer from '@app/components/ContextReminder'
import DeleteConfirmationModal from '@app/components/DeleteConfirmationModal'
import FavoriteToggle from '@app/components/FavoriteToggle'
import { actionCreators as FeedItemActionCreators } from '@app/components/FeedItem/modules/feedItems'
import { NavTabItem, NavTabs } from '@app/components/NavTabs'
import MaintenancePage from '@app/routes/Maintenance/MaintenancePage'
import { ICurrentUserState } from '@app/store/modules/account'
import { IGlobalSocialNetworksState } from '@app/store/modules/global'
import { bindFunctions } from '@app/utils/class'
import { formatPhoneNumber, humanize } from '@app/utils/formatting'
import { IconName, IconPrefix } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as React from 'react'
import DocumentTitle from 'react-document-title'
import { Link, Route, RouteComponentProps, Switch } from 'react-router-dom'
import {
  Card,
  CardBody,
  CardHeader,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from 'reactstrap'
import {
  actionCreators,
  IConnectionViewState,
} from '../modules/connectionView'
import ConnectionActivity from './ConnectionActivity'
import ConnectionConnections from './ConnectionConnections'
import ConnectionDetails from './ConnectionDetails'
import ConnectionEarnings from './ConnectionEarnings'
import ConnectionLeads from './ConnectionLeads'
import ConnectionProperties from './ConnectionProperties'

interface IConnectionViewRouterProps {
  id: string
}

type IComponentProps = IConnectionViewState &
  ICurrentUserState &
  IGlobalSocialNetworksState &
  RouteComponentProps<IConnectionViewRouterProps> &
  typeof actionCreators &
  typeof FeedItemActionCreators

export default class ConnectionViewPage extends React.PureComponent<
  IComponentProps
> {
  private connectionId: string

  constructor(props: IComponentProps) {
    super(props)

    bindFunctions(this)
  }

  public componentDidMount() {
    const { id } = this.props.match.params
    this.connectionId = id
    this.props.loadConnection(id)
  }

  public componentDidUpdate(
    prevProps: IComponentProps,
    prevState: any,
    snapshot: any,
  ) {
    const { id } = this.props.match.params
    if (prevProps.item && prevProps.item.id !== id) {
      this.connectionId = id
      this.props.loadConnection(id)
    }
  }

  public render() {
    if (!this.props.item || !this.props.item.id || !this.props.user) {
      return <div />
    }

    return (
      <DocumentTitle title={this.props.item.fullName}>
        <Container className='mt-3'>
          <Card className='d-flex flex-column mt-2'>
            <CardHeader className='bg-secondary text-white'>
              {this._renderCardHeader()}
            </CardHeader>
            <CardBody>{this._renderCardBody()}</CardBody>
          </Card>
          {this._renderTabs()}
          <Switch>
            <Route
              exact
              path={this.props.match.path}
              component={ConnectionActivity}
            />
            <Route
              exact
              path={`${this.props.match.path}/details`}
              render={() => <ConnectionDetails {...this.props} />}
            />
            <Route
              exact
              path={`${this.props.match.path}/properties`}
              render={() => <ConnectionProperties {...this.props} />}
            />
            <Route
              exact
              path={`${this.props.match.path}/connections`}
              render={() => <ConnectionConnections {...this.props} />}
            />
            <Route
              exact
              path={`${this.props.match.path}/leads`}
              render={() => <ConnectionLeads {...this.props} />}
            />
            <Route
              exact
              path={`${this.props.match.path}/earnings`}
              render={() => <ConnectionEarnings {...this.props} />}
            />
            <Route
              exact
              path={`${this.props.match.path}/reminders`}
              render={() => (
                <ContextReminderContainer
                  contextId={this.props.item.id}
                  {...this.props}
                />
              )}
            />
            <Route component={MaintenancePage} />
          </Switch>
          <DeleteConfirmationModal
            isOpen={this.props.showConfirmDelete}
            itemName={this.props.item.fullName}
            onConfirm={() => this.props.deleteConnection(this.props.item.id)}
            onToggleModal={() => this.props.toggleConfirmDelete(false)}
          />
        </Container>
      </DocumentTitle>
    )
  }

  private _renderTabs() {
    return (
      <div className='mb-3 mt-3'>
        <NavTabs>
          <NavTabItem
            exact
            iconName='chart-line'
            label='Activity'
            to={this.props.match.url}
          />
          <NavTabItem
            iconName='list'
            label='Details'
            to={`${this.props.match.url}/details`}
          />
          <NavTabItem
            badgeCount={this.props.item.properties.size}
            iconName='university'
            label='Properties'
            to={`${this.props.match.url}/properties`}
          />
          <NavTabItem
            badgeCount={this.props.item.associated.size}
            iconName='user-friends'
            label='Connections'
            to={`${this.props.match.url}/connections`}
          />
          <NavTabItem
            badgeCount={this.props.item.leads.size}
            iconName='sticky-note'
            label='Leads'
            to={`${this.props.match.url}/leads`}
          />
          <NavTabItem
            iconName='money-bill'
            label='Earnings'
            to={`${this.props.match.url}/earnings`}
          />
          <NavTabItem
            iconName='bell'
            label='Reminders'
            to={`${this.props.match.url}/reminders`}
          />
        </NavTabs>
      </div>
    )
  }

  private _renderCardBody() {
    return (
      <div className='d-flex flex-row flex-wrap'>
        <ContactProfileImage contact={this.props.item.id} size={70} />

        <div className='d-flex flex-column ml-3 align-self-center'>
          <div style={{ fontWeight: 'bold' }}>{this.props.item.fullName}</div>
          <div>
            {this.props.item.company ? this.props.item.company.name : ''}
          </div>
          <div>{this.props.item.title}</div>
        </div>

        <div className='d-flex flex-row flex-wrap mt-2 mt-md-0 ml-md-auto'>
          {this._renderPhoneNumbers()}
          {this._renderEmailAddresses()}
        </div>
      </div>
    )
  }

  private _renderCardHeader() {
    if (!this.props.user) {
      return null
    }

    const { id } = this.props.user

    let favoriteIcon: [IconPrefix, IconName] = ['far', 'star']
    let connectionHeaderIcon: [IconPrefix, IconName] = ['fal', 'user']

    const hasStarred = this.props.item.hasStarred.find((item) => {
      return item === id
    })

    if (this.props.item.isCompany) {
      connectionHeaderIcon = ['fal', 'building']
    }

    favoriteIcon = hasStarred ? ['fas', 'star'] : ['far', 'star']

    return (
      <div className='d-flex flex-row'>
        <span>
          <FontAwesomeIcon
            className='primary-color'
            icon={connectionHeaderIcon}
            fixedWidth
          />{' '}
          {this.props.item.isCompany ? 'Company' : 'Connection'}
        </span>
        <div className='ml-auto'>
          <FavoriteToggle
            className='px-1 text-primary'
            hasStarred={this.props.item.hasStarred}
            onToggleFavorite={this._onToggleFavorite}
          />
          {this._renderDropdown()}
        </div>
      </div>
    )
  }

  private _onToggleFavorite(userId: string, isFavorite: boolean) {
    this.props.toggleFavorite(this.props.item.id, userId, isFavorite)
  }

  private _renderDropdown() {
    const menuItems = []

    if (this._canManage()) {
      menuItems.push(
        <DropdownItem
          key='manage'
          onClick={() =>
            window.alert(
              'Manage connection access is not ready yet. Check back soon!',
            )
          }
        >
          Manage
        </DropdownItem>,
      )
    }

    if (this._canEdit()) {
      menuItems.push(
        <Link key='edit' to={`/connection/${this.connectionId}/edit`}>
          <DropdownItem>Edit</DropdownItem>
        </Link>,
      )
    }

    if (this._canMerge()) {
      menuItems.push(
        <DropdownItem
          key='merge'
          onClick={() =>
            window.alert('Merge connections is not ready yet. Check back soon!')
          }
        >
          Merge
        </DropdownItem>,
      )
    }

    if (this._canDelete()) {
      menuItems.push(
        <DropdownItem
          disabled={this.props.isDeleting}
          key='delete'
          onClick={() => this.props.toggleConfirmDelete(true)}
        >
          Delete
        </DropdownItem>,
      )
    }

    if (menuItems.length === 0) {
      return null
    }

    return (
      <UncontrolledDropdown style={{ display: 'inline' }}>
        <DropdownToggle tag='a' className='px-1 text-center'>
          <FontAwesomeIcon icon={['fal', 'chevron-down']} fixedWidth />
        </DropdownToggle>
        <DropdownMenu>{menuItems}</DropdownMenu>
      </UncontrolledDropdown>
    )
  }

  private _renderEmailAddresses() {
    return (
      <div>
        {this.props.item.emailAddresses.map((email: any, i) => {
          return (
            <div
              className='d-flex flex-row'
              style={{ fontSize: '0.875rem' }}
              key={`email-${i}`}
            >
              <div className='text-muted mr-2'>{humanize(email.type)}</div>
              <div className='ml-auto'>
                <a href={`mailto:${email.emailAddress}`}>
                  {email.emailAddress}
                </a>
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  private _renderPhoneNumbers() {
    return (
      <div className='mr-2'>
        {this.props.item.phoneNumbers.map((phone: any, i) => {
          return (
            <div
              className='d-flex flex-row'
              style={{ fontSize: '0.875rem' }}
              key={`phone-${i}`}
            >
              <div className='text-muted mr-2'>{humanize(phone.type)}</div>
              <div className='phone-number ml-auto'>
                {formatPhoneNumber(phone.phoneNumber)}
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  private _canMerge() {
    if (!this.props.user) {
      return false
    }

    if (this.props.user.permissions.isAdmin) {
      return true
    }
  }

  private _canDelete() {
    if (!this.props.user) {
      return false
    }

    if (this.props.permissions.canDelete) {
      if (
        this.props.item.user &&
        (!this.props.item.user.permissions.isGuest ||
          this.props.user.permissions.isGuest)
      ) {
        return false
      } else {
        return this.props.permissions.canDelete
      }
    }

    if (this.props.item.id === this.props.user.id) {
      return false
    }
  }

  private _canEdit() {
    if (!this.props.user) {
      return false
    }

    if (this.props.permissions.canEdit) {
      if (
        this.props.item.user &&
        this.props.item.id !== this.props.user.id &&
        (!this.props.item.user.permissions.isGuest ||
          this.props.user.permissions.isGuest)
      ) {
        return false
      } else {
        return this.props.permissions.canEdit
      }
    }

    if (this.props.item.id === this.props.user.id) {
      return true
    }

    if (this.props.user.permissions.isAdmin) {
      return true
    }

    return false
  }

  private _canManage() {
    if (!this.props.user) {
      return false
    }

    if (this.props.user && this.props.user.permissions.isAdmin) {
      return true
    }

    if (this.props.user.id === this.props.item.creator) {
      return true
    }

    return false
  }
}
